
import { useApiQuery, useApiMutation } from '@/hooks/use-api';
import { toast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';

// TypeScript Interfaces
export interface Review {
  _id: string;
  rating: number;
  reviewType: 'class' | 'staff' | 'class_session';
  userId: {
    _id: string;
    name: string;
    surname: string;
    email: string;
  };
  userName: string;
  classId?: {
    _id: string;
    name: string;
    label: string;
  };
  staffId?: {
    _id: string;
    name: string;
    surname: string;
  };
  feedback?: string;
  adminNotes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ReviewsResponse {
  reviews: Review[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ReviewStats {
  overall: {
    totalReviews: number;
    averageRating: number;
    ratingDistribution: Record<string, number>;
  };
  byClass: Array<{
    classId: string;
    className: string;
    avgRating: number;
    count: number;
  }>;
  byStaff: Array<{
    staffId: string;
    staffName: string;
    avgRating: number;
    count: number;
  }>;
  recentTrends: {
    last7Days: number;
    last30Days: number;
  };
}

export interface ReviewFilters {
  page?: number;
  limit?: number;
  gymId?: string;
  reviewType?: string;
  classId?: string;
  staffId?: string;
  rating?: number;
  minRating?: number;
  maxRating?: number;
  startDate?: string;
  endDate?: string;
}

export interface UpdateNotesData {
  reviewId: string;
  adminNotes: string;
}

// Get all reviews with filters
export function useReviews(filters: ReviewFilters = {}) {
  const queryParams = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== null) {
      queryParams.append(key, String(value));
    }
  });

  const queryString = queryParams.toString();
  const url = `/reviews${queryString ? `?${queryString}` : ''}`;

  return useApiQuery<ReviewsResponse>(
    ['reviews', filters],
    {
      method: 'GET',
      url,
    },
    {
      enabled: true,
      staleTime: 60 * 1000, // 1 minute
    }
  );
}

// Get a single review
export function useReview(id: string) {
  return useApiQuery<Review>(
    ['reviews', id],
    { 
      method: 'GET', 
      url: `/reviews/${id}` 
    },
    {
      enabled: !!id,
      staleTime: 60 * 1000, // 1 minute
    }
  );
}

// Get review statistics
export function useReviewStats(gymId?: string) {
  const queryParams = new URLSearchParams();
  
  if (gymId) {
    queryParams.append('gymId', gymId);
  }

  const queryString = queryParams.toString();
  const url = `/reviews/stats${queryString ? `?${queryString}` : ''}`;

  return useApiQuery<ReviewStats>(
    ['reviews', 'stats', gymId],
    {
      method: 'GET',
      url,
    },
    {
      enabled: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
}

// Update admin notes
export function useUpdateReviewNotes() {
  const queryClient = useQueryClient();

  return useApiMutation<Review, UpdateNotesData>(
    (data) => ({
      method: 'PATCH',
      url: `/reviews/${data.reviewId}/notes`,
      data: { adminNotes: data.adminNotes },
    }),
    {
      onMutate: async (variables) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({ queryKey: ['reviews', variables.reviewId] });

        // Snapshot the previous value
        const previousReview = queryClient.getQueryData<Review>(['reviews', variables.reviewId]);

        // Optimistically update to the new value
        if (previousReview) {
          queryClient.setQueryData<Review>(['reviews', variables.reviewId], {
            ...previousReview,
            adminNotes: variables.adminNotes,
          });
        }

        return { previousReview };
      },
      onError: (err, variables, context: any) => {
        // Rollback to the previous value on error
        if (context?.previousReview) {
          queryClient.setQueryData(['reviews', variables.reviewId], context.previousReview);
        }
        toast({
          title: 'Error',
          description: 'Failed to save notes',
          variant: 'destructive',
        });
      },
      onSuccess: (data, variables) => {
        toast({
          title: 'Success',
          description: 'Notes saved successfully',
        });
        // Invalidate reviews list to reflect changes
        queryClient.invalidateQueries({ queryKey: ['reviews'] });
      },
      onSettled: (data, error, variables) => {
        // Always refetch after error or success
        queryClient.invalidateQueries({ queryKey: ['reviews', variables.reviewId] });
      },
    }
  );
}

// Archive (delete) a review
export function useArchiveReview() {
  const queryClient = useQueryClient();

  return useApiMutation<void, string>(
    (reviewId) => ({
      method: 'DELETE',
      url: `/reviews/${reviewId}`,
    }),
    {
      onSuccess: (data, reviewId) => {
        toast({
          title: 'Success',
          description: 'Review archived successfully',
        });
        // Invalidate all reviews queries to refresh the list
        queryClient.invalidateQueries({ queryKey: ['reviews'] });
        // Remove the specific review from cache
        queryClient.removeQueries({ queryKey: ['reviews', reviewId] });
      },
      onError: (error) => {
        console.error('Error archiving review:', error);
        toast({
          title: 'Error',
          description: 'Failed to archive review',
          variant: 'destructive',
        });
      },
    }
  );
}
